/**
 * SolveQuestModal — a solver answers a confirm_fact or find_photo quest from
 * the Mysteries marketplace.
 *
 *   confirm_fact → provide a citation (URL and/or note). POST .../submit.
 *   find_photo   → upload a portrait, then submit the resulting photo_id.
 *                  The upload uses the mystery-granted photo endpoint, which
 *                  stores a non-primary, solver-attributed photo pending the
 *                  owner's approval.
 *
 * The submission lands in the owner's inbox; on approval the bounty (if any)
 * pays out to the solver.
 */
import { useState } from 'react'
import { api } from '../../services/api.js'

export default function SolveQuestModal({ mystery, onClose, onSolved }) {
  const kind = mystery.kind
  const [sourceUrl, setSourceUrl] = useState('')
  const [sourceNote, setSourceNote] = useState('')
  const [note, setNote] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [done, setDone] = useState(false)

  async function submit(e) {
    e.preventDefault()
    setErr(null)
    if (kind === 'confirm_fact' && !sourceUrl.trim() && !sourceNote.trim()) {
      setErr('Provide a source URL or a note.')
      return
    }
    if (kind === 'find_photo' && !file) {
      setErr('Choose a photo to upload.')
      return
    }
    setBusy(true)
    try {
      if (kind === 'confirm_fact') {
        await api.post(`/mysteries/${mystery.id}/submit`, {
          source_url: sourceUrl.trim() || null,
          source_note: sourceNote.trim() || null,
        })
      } else {
        const fd = new FormData()
        fd.append('photo', file)
        const photo = await api.upload(`/persons/${mystery.person_id}/photos`, fd)
        await api.post(`/mysteries/${mystery.id}/submit`, {
          photo_id: photo.id,
          note: note.trim() || null,
        })
      }
      setDone(true)
      onSolved?.()
    } catch (e2) {
      setErr(e2.message || 'Could not submit your answer')
      setBusy(false)
    }
  }

  const heading = kind === 'confirm_fact' ? 'Verify this fact' : 'Submit a photo'

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal request-research-modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>{done ? 'Answer submitted' : heading}</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        {done ? (
          <div className="request-research-modal__body">
            <p className="request-research-modal__intro">
              Thanks — your answer is now with the owner for review. If it’s
              approved and the mystery carries a bounty, you’ll be paid out
              automatically once your payout account is set up.
            </p>
            <div className="request-research-modal__actions">
              <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="request-research-modal__body">
            {kind === 'confirm_fact' ? (
              <>
                <p className="request-research-modal__intro">
                  Back this claim with a source — a link to a record, archive
                  page, or a short note on where it can be verified.
                </p>
                <label className="request-research-modal__field">
                  <span>Source URL <em>(optional)</em></span>
                  <input
                    type="url" value={sourceUrl}
                    onChange={e => setSourceUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </label>
                <label className="request-research-modal__field">
                  <span>Note <em>(optional)</em></span>
                  <textarea
                    rows={3} value={sourceNote} maxLength={2000}
                    onChange={e => setSourceNote(e.target.value)}
                    placeholder="e.g. Confirmed in the 1881 civil register, Rotterdam."
                  />
                </label>
              </>
            ) : (
              <>
                <p className="request-research-modal__intro">
                  Upload the portrait you found. It’s attached to this person as
                  a pending, credited photo until the owner approves it.
                </p>
                <label className="request-research-modal__field">
                  <span>Photo</span>
                  <input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
                </label>
                <label className="request-research-modal__field">
                  <span>Note <em>(optional)</em></span>
                  <input
                    type="text" value={note} maxLength={300}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Where it came from"
                  />
                </label>
              </>
            )}

            {err && <div className="request-research-modal__err">{err}</div>}

            <div className="request-research-modal__actions">
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Submitting…' : 'Submit answer →'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
